import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useSpring as useSp } from "framer-motion";
import { maskReveal } from "@/lib/motion";

export const GrainOverlay = () => <div className="pp-grain" aria-hidden="true" />;

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSp(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return (
    <motion.div
      data-testid="scroll-progress"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[2px] z-[70] origin-left"
    >
      <div className="h-full w-full" style={{ background: "linear-gradient(90deg,#f59e0b,#ec4899,#8b5cf6,#06b6d4)" }} />
    </motion.div>
  );
};

export const SectionHeading = ({ overline, title, sub, align = "left", light = false, testid }) => (
  <div className={`mb-14 md:mb-20 ${align === "center" ? "text-center mx-auto max-w-3xl" : "max-w-4xl"}`}>
    {overline && (
      <motion.p
        initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.5 }}
        className="pp-overline mb-5"
      >
        {overline}
      </motion.p>
    )}
    <div className="overflow-hidden">
      <motion.h2
        data-testid={testid}
        variants={maskReveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
        className="text-4xl md:text-5xl lg:text-7xl uppercase tracking-tight leading-[0.92] font-display font-600 text-white"
      >
        {title}
      </motion.h2>
    </div>
    {sub && (
      <motion.p
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}
        className={`mt-6 text-base md:text-lg leading-relaxed max-w-2xl ${light ? "text-slate-200" : "text-slate-400"} ${align === "center" ? "mx-auto" : ""}`}
      >
        {sub}
      </motion.p>
    )}
  </div>
);

export const Marquee = ({ text = "CRUNCHING DIGITAL GROWTH", className = "" }) => (
  <div className={`overflow-hidden py-6 select-none ${className}`} aria-hidden="true">
    <div className="pp-marquee">
      {[0, 1].map((k) => (
        <span key={k} className="flex shrink-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="pp-outline font-display uppercase text-6xl md:text-8xl font-700 px-8 tracking-tight">
              {text}
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

export const MagneticButton = ({ children, className = "", strength = 0.4, as = "button", ...props }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };

  const Comp = motion[as] || motion.button;
  return (
    <Comp
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={className}
      {...props}
    >
      {children}
    </Comp>
  );
};
